import { CommonModule, NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatMenuModule } from "@angular/material/menu";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { LangDefinition, TranslocoService } from "@jsverse/transloco";
import { CookieService } from "app/core/services/cookie.service";
import { Subject, take, takeUntil } from "rxjs";

@Component({
  selector: "languages",
  templateUrl: "./languages.component.html",
  styleUrls: ["./languages.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: "languages",
  imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet, CommonModule],
})
export class LanguagesComponent implements OnInit, OnDestroy {
  private readonly userLanguageCookieKey = "user_selected_language_code";
  private readonly languageMap = {
    de: "Deutsch",
    "en-GB": "Englisch",
  };
  private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

  availableLangs: any;
  activeLang: string;
  flagCodes: Record<string, string>;
  languageMenuOpened = false;

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _fuseNavigationService: FuseNavigationService,
    private readonly _translocoService: TranslocoService,
    private readonly _cookieService: CookieService,
  ) {}

  ngOnInit(): void {
    // Get the available languages from transloco
    this.availableLangs = this._translocoService.getAvailableLangs();

    this._translocoService.langChanges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((activeLang) => {
        this.activeLang = activeLang;
        this._updateNavigation(activeLang);
        this._changeDetectorRef.markForCheck(); // required with OnPush
      });

    // Set the country ISO codes for flags
    // this.flagCodes = {
    //     de: 'de', // Germany
    //     en: 'gb', // UK English (use GB for Union Jack)
    //     fr: 'fr', // France
    // };
    this.flagCodes = {
      "en-GB": "GB", // UK flag
      // 'en-US': 'US', // US flag
      de: "DE",
      fr: "FR",
    };
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // Helpers so template never breaks on "string | LangDefinition"
  getLangId(lang: string | LangDefinition): string {
    return typeof lang === "string" ? lang : lang.id;
  }

  getLangLabel(lang: string | LangDefinition): string {
    return typeof lang === "string" ? lang : lang.label;
  }

  setActiveLang(lang: string | LangDefinition): void {
    const id = this.getLangId(lang);
    const langCode = this.languageMap[id] || "de";
    localStorage.setItem("userLang", langCode);
    this._cookieService.setCookie(this.userLanguageCookieKey, id);
    this._translocoService.setActiveLang(id);
  }

  trackByFn(index: number, lang: string | LangDefinition): any {
    return typeof lang === "string" ? lang : lang.id || index;
  }

  private _updateNavigation(lang: string): void {
    const navComponent =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        "mainNavigation",
      );

    if (!navComponent) {
      return;
    }

    const navigation = navComponent.navigation;

    const projectDashboardItem = this._fuseNavigationService.getItem(
      "dashboards.project",
      navigation,
    );
    if (projectDashboardItem) {
      this._translocoService
        .selectTranslate("Project")
        .pipe(take(1))
        .subscribe((translation) => {
          projectDashboardItem.title = translation;
          navComponent.refresh();
        });
    }

    const analyticsDashboardItem = this._fuseNavigationService.getItem(
      "dashboards.analytics",
      navigation,
    );
    if (analyticsDashboardItem) {
      this._translocoService
        .selectTranslate("Analytics")
        .pipe(take(1))
        .subscribe((translation) => {
          analyticsDashboardItem.title = translation;
          navComponent.refresh();
        });
    }
  }
}
